import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { StoreProvider } from '@pg-app/core/services/storeProvider/store.provider';
import { environment } from '@pg-env/environment';
import { Observable } from 'rxjs';
import { PortalType } from '@pg-app/core/Enum/Authentication/PortalType';
import { FundAccountService } from '@pg-app/client/client-dashboard/components/fund-account/service/fund-account.service';
import { Router } from '@angular/router';
import { RolesService } from '@pg-app/core/services/roles/role.servirce';
import { MatDialog } from '@angular/material/dialog';

interface IUserInfo {
  email: string;
  password: string;
  portalType: PortalType;
}

interface ISendTicketDto {
  email: string;
  clientId;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  baseURL = environment.identityApiUrl;
  accessToken = '';
  refreshToken = '';
  tokenType = 'Bearer';
  userId = '';
  clientId = '';
  mainUserId = '';

  public jwtHelper = new JwtHelperService();
  cachedRequests: Array<HttpRequest<any>> = [];
  constructor(private http: HttpClient,
    private store: StoreProvider,
    private fundAccountService: FundAccountService,
    private router: Router,
    private roleService: RolesService,
    private dialog: MatDialog
  ) {
    this.accessToken = this.store.get('accessToken');
    this.tokenType = this.store.get('tokenType') || 'Bearer';
    this.refreshToken = this.store.get('refreshToken');
    this.userId = this.store.get('userId');
    this.clientId = this.store.get('clientId');
    this.mainUserId = this.store.get('mainUserId');
  }

  async isAuthenticated() {
    if (!this.accessToken) {
      return false;
    }

    if (this.jwtHelper.isTokenExpired(this.accessToken)) {
      const res = await this.refreshAccessToken().toPromise();
      if (res) {
        this.updateAccessToken(res);
        return this.accessToken && !this.jwtHelper.isTokenExpired(this.accessToken);
      }
    } else {
      return this.accessToken && !this.jwtHelper.isTokenExpired(this.accessToken);
    }
  }

  logout(removeToken: boolean = true) {
    if (removeToken) {
      const data = {
        userId: this.store.get('userId'),
        clientId: this.store.get('clientId'),
        refreshToken: this.store.get('refreshToken')
      };

      this.http.post(`${this.baseURL}/account/log-out`, data).subscribe(_success => { });
    }

    if (sessionStorage.getItem('clickBuyNow') === 'false' && sessionStorage.getItem('clickGetRate') === 'true') {
      this.fundAccountService.sendEmailQuoteAlerts(JSON.parse(sessionStorage.getItem('newTrade'))).subscribe();
      sessionStorage.clear();
    }

    // keep state of show barclay dialog
    const showedBarclayNotification = this.store.get('showedBarclaysNotificationSecondTime');
    setTimeout(() => {
      // clean up data
      this.accessToken = '';
      this.store.clearAll();
      this.store.set('showedBarclaysNotificationSecondTime', showedBarclayNotification);
      this.roleService.reload = true;
      this.dialog.closeAll();
      this.router.navigate(['auth/login']);
    }, 0);
  }

  authenticate(userInfo: IUserInfo): Observable<any> {
    return this.http.post(`${this.baseURL}/account/login-account`, userInfo, {
      headers: {
        useLocalHandleException: 'Y'
      }
    });
  }

  checkUncodedAccount(email: string): Observable<any> {
    return this.http.post(`${environment.customerApiUrl}/customer/check-uncoded-accounts`, {email: email}, {
      headers: {
        useLocalHandleException: 'Y'
      }
    });
  }

  sendReactivateAccountTicket(sendTicketDto: ISendTicketDto): Observable<any> {
    // TODO: apply with latest API from backend
    return this.http.post(`${this.baseURL}/account/account-deactivated`, sendTicketDto);
  }

  refreshAccessToken(): Observable<any> {
    const userId = this.store.get('userId');
    const clientId = this.store.get('clientId');
    const refreshToken = this.store.get('refreshToken');

    if (!userId || !clientId || !refreshToken) {
      return;
    }

    const param = {
      userId: userId,
      clientId: clientId,
      refreshToken: refreshToken
    }

    return this.http.post(`${this.baseURL}/account/refresh-access-token`, param);
  }

  public collectFailedRequest(request): void {
    this.cachedRequests.push(request);
  }

  public retryFailedRequests(): void {

    this.cachedRequests.forEach(request => {
      request = request.clone({
        setHeaders: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.accessToken}`
        }
      });

    });
  }

  updateAccessToken(data: any) {
    this.store.set('accessToken', data.token);
    this.store.set('refreshToken', data.refreshToken);
    this.store.set('tokenType', data.tokenType);
    this.store.set('userId', data.userId);
    this.store.set('clientId', data.clientId);
    this.store.set('role', data.roles);
    this.store.set('mainUserId', data.mainUserId);

    this.accessToken = this.store.get('accessToken');
    this.tokenType = this.store.get('tokenType') || 'Bearer';
    this.refreshToken = this.store.get('refreshToken');
    this.userId = this.store.get('userId');
    this.clientId = this.store.get('clientId');
  }
}
